import { buildSearchIndex, querySearchIndex, } from './search-index.js';
/**
 * Owns a book's search index. Runs queries in a Worker when one is available,
 * otherwise synchronously on the calling thread. Only the most recent query's
 * result resolves; earlier in-flight queries resolve to `[]`.
 */
export class SearchController {
    #worker = null;
    #index = null;
    #ready = null;
    #seq = 0;
    #pending = new Map();
    constructor(opts = {}) {
        const factory = opts.workerFactory === false
            ? null
            : (opts.workerFactory ?? defaultWorkerFactory());
        if (factory) {
            try {
                this.#worker = factory();
                this.#worker.addEventListener('message', (ev) => {
                    const msg = ev.data;
                    if (msg.type === 'result') {
                        this.#pending.get(msg.id)?.(msg.hits);
                        this.#pending.delete(msg.id);
                    }
                });
            }
            catch {
                this.#worker = null;
            }
        }
    }
    get usingWorker() {
        return this.#worker !== null;
    }
    build(sections) {
        if (this.#worker) {
            const w = this.#worker;
            this.#ready = new Promise((resolve) => {
                const onBuilt = (ev) => {
                    if (ev.data.type === 'built') {
                        w.removeEventListener('message', onBuilt);
                        resolve();
                    }
                };
                w.addEventListener('message', onBuilt);
                this.#send({ type: 'build', sections });
            });
        }
        else {
            this.#index = buildSearchIndex(sections);
            this.#ready = Promise.resolve();
        }
        return this.#ready;
    }
    async query(query, opts) {
        const id = ++this.#seq;
        await this.#ready;
        if (this.#worker) {
            return new Promise((resolve) => {
                this.#pending.set(id, resolve);
                this.#send({ type: 'query', id, query, ...(opts ? { opts } : {}) });
            }).then((hits) => (id === this.#seq ? hits : []));
        }
        const hits = this.#index ? querySearchIndex(this.#index, query, opts) : [];
        return id === this.#seq ? hits : [];
    }
    destroy() {
        this.#worker?.terminate();
        this.#worker = null;
        this.#index = null;
        this.#pending.clear();
    }
    #send(msg) {
        this.#worker?.postMessage(msg);
    }
}
function defaultWorkerFactory() {
    if (typeof Worker === 'undefined')
        return null;
    return () => 
    // `search-worker` sits next to this module in `dist/search/`; bundlers
    // pick up this `new Worker(new URL(...))` form and emit a worker chunk.
    new Worker(new URL('./search-worker.js', import.meta.url), {
        type: 'module',
        name: 'pore-search',
    });
}
//# sourceMappingURL=search-controller.js.map